import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Fn_AddEditData, Fn_FillListData } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import { toast } from "react-toastify";

// ─── Column Definitions — FirmMaster + BranchMaster fields ───────────────────
const COLS = [
  // ── Firm fields ──────────────────────────────────────────────────────────
  { key: "Name",                label: "Firm Name *",        type: "text",    w: 180 },
  { key: "F_FirmType",          label: "Firm Type",          type: "select",  optKey: "FirmType",     w: 150, placeholder: "-- Select Firm Type --" },
  { key: "F_ConcernType",       label: "Concern Type",       type: "select",  optKey: "ConcernType",  w: 140, placeholder: "-- Select Concern Type --" },
  { key: "F_PremisesType",      label: "Premises Type",      type: "select",  optKey: "PremisesType", w: 140, placeholder: "-- Select Premises Type --" },
  { key: "NatureOfBusiness",    label: "Nature of Business", type: "text",    w: 170 },
  { key: "MaxWorkers",          label: "Max Workers",        type: "number",  w: 100 },
  { key: "UseOfPower",          label: "Use of Power",       type: "text",    w: 120 },
  { key: "InTime",              label: "In Time",            type: "time",    w: 110 },
  { key: "OutTime",             label: "Out Time",           type: "time",    w: 110 },
  { key: "Signature",           label: "Signature",          type: "text",    w: 130 },
  { key: "CentralRuleMinWages", label: "Central Min Wages",  type: "boolean", w: 130, placeholder: "-- Select Yes / No --" },
  { key: "LicenceFee",          label: "Licence Fee",        type: "number",  w: 100 },
  { key: "DifferenceFee",       label: "Difference Fee",     type: "number",  w: 110 },
  { key: "AmendmentFee",        label: "Amendment Fee",      type: "number",  w: 110 },
  { key: "DuplicateLicenceFee", label: "Dup. Lic. Fee",      type: "number",  w: 110 },
  { key: "Remark",              label: "Remark",             type: "text",    w: 200 },
  // ── Branch fields (one branch per firm; main branch by default) ──────────
  { key: "BranchName",              label: "Branch Name",           type: "text",   w: 180 },
  { key: "CoAddress",               label: "C/O Address",           type: "text",   w: 200 },
  { key: "OfficeAddress",           label: "Office Address",        type: "text",   w: 200 },
  { key: "PhoneMobileNo",           label: "Phone / Mobile",        type: "text",   w: 140 },
  { key: "PFNo",                    label: "PF No",                 type: "text",   w: 140 },
  { key: "ESINo",                   label: "ESI No",                type: "text",   w: 140 },
  { key: "LINNo",                   label: "LIN No",                type: "text",   w: 130 },
  { key: "FBRegNo",                 label: "FB Reg No",             type: "text",   w: 130 },
  { key: "RegRenDate",              label: "Reg. Renewal Date",     type: "date",   w: 140 },
  { key: "RegExpiryDate",           label: "Reg. Expiry Date",      type: "date",   w: 140 },
  { key: "PFUserName",              label: "PF Username",           type: "text",   w: 130 },
  { key: "PFPassword",              label: "PF Password",           type: "text",   w: 130 },
  { key: "ESIUserName",             label: "ESI Username",          type: "text",   w: 130 },
  { key: "ESIPassword",             label: "ESI Password",          type: "text",   w: 130 },
  { key: "FactoryActUserName",      label: "Factory Act Username",  type: "text",   w: 150 },
  { key: "FactoryActPassword",      label: "Factory Act Password",  type: "text",   w: 150 },
  { key: "EmploymentUserName",      label: "Employment Username",   type: "text",   w: 150 },
  { key: "EmploymentPassword",      label: "Employment Password",   type: "text",   w: 150 },
  { key: "LabourUserName",          label: "Labour Username",       type: "text",   w: 140 },
  { key: "LabourPassword",          label: "Labour Password",       type: "text",   w: 140 },
];

const emptyRow = () => ({
  Id: 0, BranchId: 0,
  ...Object.fromEntries(COLS.map((c) => [c.key, c.type === "boolean" ? "false" : ""])),
});

// Map an API firm object → grid row format
const apiRowToGridRow = (r) => ({
  Id:       r.Id      ?? 0,
  BranchId: r.BranchId ?? 0,
  ...Object.fromEntries(COLS.map((col) => {
    let val = r[col.key];
    if (col.type === "boolean") val = val === true || val === "true" ? "true" : "false";
    else if (col.type === "date" && val) val = String(val).substring(0, 10); // strip time part
    else val = val === null || val === undefined ? "" : String(val);
    return [col.key, val];
  })),
});

// ─── Pure helpers ─────────────────────────────────────────────────────────────
const getSelRange = (foc, anc) => ({
  r1: Math.min(foc.r, anc.r),
  r2: Math.max(foc.r, anc.r),
  c1: Math.min(foc.c, anc.c),
  c2: Math.max(foc.c, anc.c),
});

const colLetter = (c) => {
  let s = "";
  let n = c + 1;
  while (n > 0) {
    s = String.fromCharCode(64 + ((n - 1) % 26 + 1)) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

// ─── Static styles ────────────────────────────────────────────────────────────
const KBD = {
  background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: "3px",
  padding: "1px 5px", fontSize: "10px", fontFamily: "monospace", color: "#374151",
};
const TH_NUM = {
  width: "40px", minWidth: "40px", background: "#cbd5e1",
  border: "1px solid #adb5bd", textAlign: "center", fontSize: "11px",
  color: "#64748b", height: "28px", position: "sticky", top: 0, zIndex: 10,
  boxSizing: "border-box",
};
const thCol = (w, inColSel) => ({
  minWidth: `${w}px`, width: `${w}px`,
  background: inColSel ? "#1b5e20" : "#217346",
  color: "#fff", border: "1px solid #1b5e20", padding: "0 6px",
  fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden",
  textOverflow: "ellipsis", height: "28px", position: "sticky", top: 0,
  zIndex: 5, boxSizing: "border-box", userSelect: "none",
});
const rnTd = (ri, focR) => ({
  width: "40px", minWidth: "40px",
  background: focR === ri ? "#dbeafe" : "#f1f5f9",
  border: "1px solid #d0d0d0", textAlign: "center", fontSize: "11px",
  color: "#64748b", height: "28px", fontWeight: focR === ri ? 700 : 400,
  padding: 0, position: "sticky", left: 0, zIndex: 2, userSelect: "none",
});
const tdStyle = (isFoc, isSel, ri, isEditFoc = false, isDupe = false, rowBg = null) => ({
  height: "28px", padding: isEditFoc ? 0 : "0 3px",
  border: isDupe ? "2px solid #dc3545" : isFoc ? "2px solid #1565c0" : isSel ? "1px solid #90caf9" : "1px solid #d0d0d0",
  background: isDupe ? (isFoc ? "#fff5f5" : "#fff0f0") : isFoc ? "#ffffff" : isSel ? "#bbdefb" : rowBg ?? (ri % 2 === 0 ? "#ffffff" : "#f8f9fa"),
  cursor: "cell", position: "relative", zIndex: isFoc ? 4 : isSel ? 2 : 1,
  boxSizing: "border-box", overflow: "hidden", verticalAlign: "middle",
});

// Column group definitions (for column-visibility picker)
const COL_GROUPS = [
  { label: "Firm Fields",   startKey: "Name",       endKey: "Remark",         color: "#1565c0" },
  { label: "Branch Fields", startKey: "BranchName", endKey: "LabourPassword", color: "#bf360c" },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const MultiFirmEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [state,setState] = useState({});
  const [rows,      setRows]      = useState(() => Array.from({ length: 10 }, emptyRow));
  const [focus,     setFocus]     = useState({ r: 0, c: 0 }); // active cell
  const [anchor,    setAnchor]    = useState({ r: 0, c: 0 }); // selection-range start
  const [editMode,      setEditMode]      = useState(false);           // true = cursor inside cell (like Excel F2)
  const [visibleKeys,   setVisibleKeys]   = useState(() => new Set(COLS.map(c => c.key)));
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [unlockedIds,   setUnlockedIds]   = useState(new Set()); // Id>0 rows unlocked for editing
  const [dropdowns, setDropdowns] = useState({ FirmType: [], ConcernType: [], PremisesType: [] });
  const [pageSize,    setPageSize]    = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Stable refs for callbacks (avoid stale closure)
  const rowsRef     = useRef(rows);
  const focusRef    = useRef(focus);
  const anchorRef   = useRef(anchor);
  const editModeRef     = useRef(false);
  const editModeTypeRef = useRef("typing"); // "f2" = F2/dblclick (arrows move cursor); "typing" = typed char (arrows navigate cells)
  const editOrigRef     = useRef("");   // original value before edit started (for Escape restore)
  const inputRef        = useRef(null);
  const gridRef         = useRef(null);
  const focusedCellRef  = useRef(null);
  const openDropdownRef = useRef(false); 
  // set true when Space pressed → opens select
  const colPickerRef    = useRef(null);
  const visColsRef      = useRef(COLS);
  const historyRef      = useRef([]); // undo stack (rows snapshots)
  const futureRef       = useRef([]); // redo stack
  const MAX_HISTORY     = 60;
  const currentPageRef  = useRef(currentPage);

  useEffect(() => { rowsRef.current     = rows;     }, [rows]);
  useEffect(() => { focusRef.current    = focus;    }, [focus]);
  useEffect(() => { anchorRef.current   = anchor;   }, [anchor]);
  useEffect(() => { editModeRef.current = editMode; }, [editMode]);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  // Visible columns derived from visibleKeys (updated every render)
  const visCOLS = COLS.filter(c => visibleKeys.has(c.key));
  visColsRef.current = visCOLS;

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(rows.length, currentPage * pageSize);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  useEffect(() => {
    const focusRow = focus.r;
    const pageNum = Math.floor(focusRow / pageSize) + 1;
    if (pageNum !== currentPageRef.current && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  }, [focus.r, pageSize, totalPages]);

  // Clamp focus/anchor when columns are hidden
  useEffect(() => {
    const maxC = visColsRef.current.length - 1;
    if (maxC < 0) return;
    setFocus(f => ({ ...f, c: Math.min(f.c, maxC) }));
    setAnchor(a => ({ ...a, c: Math.min(a.c, maxC) }));
    setEditMode(false);
  }, [visibleKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close column picker on outside click
  useEffect(() => {
    if (!colPickerOpen) return;
    const handleOutside = (e) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setColPickerOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [colPickerOpen]);

  // Load dropdown data + existing firm rows
  useEffect(() => {
    const fetchAll = async () => {
      await Promise.all([
        Fn_FillListData(dispatch, setDropdowns, "FirmType",    `${API_WEB_URLS.MASTER}/0/token/FirmTypeMaster/Id/0`),
        Fn_FillListData(dispatch, setDropdowns, "ConcernType", `${API_WEB_URLS.MASTER}/0/token/ConcernTypeMaster/Id/0`),
        Fn_FillListData(dispatch, setDropdowns, "PremisesType",`${API_WEB_URLS.MASTER}/0/token/PremisesTypeMaster/Id/0`),
      ]);
      await FetchAllFirms();
    };
    fetchAll();
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps


  const FetchAllFirms = async () => {
    const res = await Fn_FillListData(dispatch, setDropdowns, "firmData",
      `${API_WEB_URLS.MASTER}/0/token/FirmDataAll/Id/0`);
    if (res && res.length > 0) {
      const loaded = res.map(apiRowToGridRow);
      // Pad with empty rows so there are at least 5 blank rows after existing data
      const blanks = Array.from({ length: 5 }, emptyRow);
      setRows([...loaded, ...blanks]);
    }
  };
  // Auto-scroll: keep focused cell visible inside the scroll container
  useEffect(() => {
    const container = gridRef.current;
    const cell      = focusedCellRef.current;
    if (!container || !cell) return;

    const cRect = container.getBoundingClientRect();
    const eRect = cell.getBoundingClientRect();

    // Horizontal — account for sticky S.No column (40 px)
    const stickyLeft = 40;
    if (eRect.left < cRect.left + stickyLeft) {
      container.scrollLeft -= cRect.left + stickyLeft - eRect.left;
    } else if (eRect.right > cRect.right) {
      container.scrollLeft += eRect.right - cRect.right;
    }

    // Vertical — account for sticky header row (28 px)
    const stickyTop = 28;
    if (eRect.top < cRect.top + stickyTop) {
      container.scrollTop -= cRect.top + stickyTop - eRect.top;
    } else if (eRect.bottom > cRect.bottom) {
      container.scrollTop += eRect.bottom - cRect.bottom;
    }
  }, [focus]);

  // Focus management: edit mode → focus input at end; nav mode → focus grid container
  useEffect(() => {
    if (editMode) {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value?.length ?? 0;
      const supportsSelection = el.setSelectionRange && el.tagName !== "SELECT" && el.type !== "time" && el.type !== "number";
      if (supportsSelection) {
        try { el.setSelectionRange(len, len); } catch (_) { /* some browsers may still throw */ }
      }
      // Space was pressed → open the dropdown via showPicker() (the only reliable
      // cross-browser way to programmatically open a <select> dropdown)
      if (openDropdownRef.current && el.tagName === "SELECT") {
        openDropdownRef.current = false;
        try { el.showPicker(); } catch (_) { /* fallback: select is focused, user can press Space again */ }
      }
    } else {
      gridRef.current?.focus();
    }
  }, [focus, editMode]);

  // ── Derived selection ──────────────────────────────────────────────────────
  const selRange = getSelRange(focus, anchor);
  const inSel    = (r, c) =>
    r >= selRange.r1 && r <= selRange.r2 && c >= selRange.c1 && c <= selRange.c2;
  const isFoc    = (r, c) => focus.r === r && focus.c === c;

  // ── Ensure rows array is long enough ──────────────────────────────────────
  const ensureRows = useCallback((needed) => {
    setRows((prev) => {
      if (prev.length >= needed) return prev;
      return [...prev, ...Array.from({ length: needed - prev.length }, emptyRow)];
    });
  }, []);

  // ── Navigate to cell (r, c); always exits edit mode; extendSel = Shift held ──
  const gotoCell = useCallback((r, c, extendSel = false) => {
    const nr = Math.max(0, r);
    const nc = Math.max(0, Math.min(c, visColsRef.current.length - 1));
    if (nr >= rowsRef.current.length) ensureRows(nr + 1);
    if (!extendSel) setAnchor({ r: nr, c: nc });
    setFocus({ r: nr, c: nc });
    setEditMode(false);
  }, [ensureRows]);

  const handlePageChange = useCallback((newPage) => {
    const totalP = Math.max(1, Math.ceil(rowsRef.current.length / pageSize));
    if (newPage < 1 || newPage > totalP) return;
    setCurrentPage(newPage);
    const newRow = (newPage - 1) * pageSize;
    setFocus(f => ({ r: newRow, c: f.c }));
    setAnchor(a => ({ r: newRow, c: a.c }));
  }, [pageSize]);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setFocus(f => ({ r: 0, c: f.c }));
    setAnchor(a => ({ r: 0, c: a.c }));
  }, []);

  const addNewRow = useCallback(() => {
    setRows(prev => {
      const next = [...prev, emptyRow()];
      const newIdx = next.length - 1;
      setTimeout(() => {
        setFocus({ r: newIdx, c: 0 });
        setAnchor({ r: newIdx, c: 0 });
        setEditMode(true);
      }, 0);
      return next;
    });
  }, []);

  // ── Push current rows to undo stack (call BEFORE any mutation) ────────────
  const pushHistory = useCallback(() => {
    historyRef.current = [...historyRef.current.slice(-(MAX_HISTORY - 1)), rowsRef.current];
    futureRef.current  = []; // new action clears redo stack
  }, []);

  // ── Undo last action ──────────────────────────────────────────────────────
  const doUndo = useCallback(() => {
    if (!historyRef.current.length) return;
    futureRef.current = [rowsRef.current, ...futureRef.current.slice(0, MAX_HISTORY - 1)];
    const prev = historyRef.current[historyRef.current.length - 1];
    historyRef.current = historyRef.current.slice(0, -1);
    setRows(prev);
    setEditMode(false);
  }, []);

  // ── Redo last undone action ───────────────────────────────────────────────
  const doRedo = useCallback(() => {
    if (!futureRef.current.length) return;
    historyRef.current = [...historyRef.current.slice(-(MAX_HISTORY - 1)), rowsRef.current];
    const next = futureRef.current[0];
    futureRef.current = futureRef.current.slice(1);
    setRows(next);
    setEditMode(false);
  }, []);

  // ── Mutate one cell ────────────────────────────────────────────────────────
  const setCell = useCallback((r, key, val) => {
    pushHistory();
    setRows((prev) => prev.map((row, i) => (i === r ? { ...row, [key]: val } : row)));
  }, [pushHistory]);

  // ── Enter edit mode (F2 / double-click / typing) ──────────────────────────
  // overwriteChar: non-null = overwrite cell with this char (typed key); null = keep existing value
  const enterEditMode = useCallback((r, c, overwriteChar = null) => {
    const col = visColsRef.current[c];
    if (!col) return;
    // Block editing for locked existing rows
    const row = rowsRef.current[r];
    if (row && row.Id > 0 && !unlockedIds.has(row.Id)) return;
    editOrigRef.current = rowsRef.current[r]?.[col.key] ?? "";
    // Track HOW we entered edit mode:
    // "f2"     → F2 or double-click → arrows move cursor inside text (native)
    // "typing" → user typed a char  → arrows still navigate cells
    editModeTypeRef.current = overwriteChar === null ? "f2" : "typing";
    if (overwriteChar !== null && (col.type === "text" || col.type === "number" || col.type === "time")) {
      setCell(r, col.key, overwriteChar);
    }
    setEditMode(true);
  }, [setCell, unlockedIds]);

  // ── Display value in read-only cell ───────────────────────────────────────
  const displayVal = (col, val) => {
    if (col.type === "select") {
      const opt = (dropdowns[col.optKey] || []).find((o) => String(o.Id) === String(val));
      return opt ? opt.Name : "";
    }
    if (col.type === "boolean") return val === "true" ? "Yes" : "No";
    return val ?? "";
  };

  // ── Multi-cell paste (Excel tab+newline format) ────────────────────────────
  const doPaste = useCallback((text, startR, startC) => {
    if (!text) return;
    pushHistory();
    const pasted = text.replace(/\r/g, "").trimEnd().split("\n").map((l) => l.split("\t"));
    setRows((prev) => {
      const next = [...prev];
      while (next.length < startR + pasted.length) next.push(emptyRow());
      pasted.forEach((cols, dr) => {
        cols.forEach((val, dc) => {
          const tc = startC + dc;
          if (tc < visColsRef.current.length)
            next[startR + dr] = { ...next[startR + dr], [visColsRef.current[tc].key]: val.trim() };
        });
      });
      return next;
    });
  }, [pushHistory]);

  // ── Copy selection to clipboard ────────────────────────────────────────────
  const doCopy = useCallback(() => {
    const { r1, r2, c1, c2 } = getSelRange(focusRef.current, anchorRef.current);
    const lines = [];
    for (let ri = r1; ri <= r2; ri++) {
      const cells = [];
      for (let ci = c1; ci <= c2; ci++)
        cells.push(String(rowsRef.current[ri]?.[visColsRef.current[ci].key] ?? ""));
      lines.push(cells.join("\t"));
    }
    navigator.clipboard?.writeText(lines.join("\n"));
  }, []);

  // ── Clear selected range ───────────────────────────────────────────────────
  const clearSel = useCallback(() => {
    pushHistory();
    const { r1, r2, c1, c2 } = getSelRange(focusRef.current, anchorRef.current);
    setRows((prev) => {
      const next = prev.map((row) => ({ ...row }));
      for (let ri = r1; ri <= r2; ri++) {
        const row = next[ri];
        if (row.Id > 0 && !unlockedIds.has(row.Id)) continue; // skip locked rows
        for (let ci = c1; ci <= c2; ci++) {
          const col = visColsRef.current[ci];
          if (col) next[ri][col.key] = col.type === "boolean" ? "false" : "";
        }
      }
      return next;
    });
  }, [unlockedIds, pushHistory]);

  // ── Select all ─────────────────────────────────────────────────────────────
  const selectAll = useCallback(() => {
    setAnchor({ r: 0, c: 0 });
    setFocus({ r: rowsRef.current.length - 1, c: visColsRef.current.length - 1 });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // MASTER KEYBOARD HANDLER — used by both cell inputs AND grid container
  // Two modes exactly like Excel:
  //   NAV MODE  : arrows move between cells, typing starts overwrite
  //   EDIT MODE : arrows move cursor inside input; Up/Down exit+navigate
  // ─────────────────────────────────────────────────────────────────────────
  const handleKey = useCallback((e) => {
    const { r, c } = focusRef.current;
    const col        = visColsRef.current[c];
    if (!col) return;
    const isTextLike = col.type === "text" || col.type === "number" || col.type === "time" || col.type === "date";
    const shift      = e.shiftKey;
    const ctrl       = e.ctrlKey || e.metaKey;
    const inEdit     = editModeRef.current;

    // Ctrl shortcuts
    if (ctrl) {
      if (inEdit) {
        // In edit mode: Ctrl+A selects all text in the input (native browser) — don't intercept
        // Ctrl+C / Ctrl+V also pass through so the browser handles them on the input
        return;
      }
      if (e.key.toLowerCase() === "c") { doCopy(); return; }
      if (e.key.toLowerCase() === "v") {
        e.preventDefault();
        navigator.clipboard?.readText().then((t) => doPaste(t, r, c));
        return;
      }
      if (e.key.toLowerCase() === "a") { e.preventDefault(); selectAll(); return; }
      if (e.key.toLowerCase() === "z") { e.preventDefault(); doUndo(); return; }
      if (e.key.toLowerCase() === "y") { e.preventDefault(); doRedo(); return; }
      return;
    }

    // ── EDIT MODE ──────────────────────────────────────────────────────────
    if (inEdit) {
      const isF2Mode = editModeTypeRef.current === "f2";

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          setCell(r, col.key, editOrigRef.current); // restore original value
          setEditMode(false);
          break;

        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          gotoCell(r + 1, c); // exits edit mode via gotoCell
          break;

        case "Tab":
          e.preventDefault();
          e.stopPropagation();
          if (shift) { c === 0 ? gotoCell(r - 1, visColsRef.current.length - 1) : gotoCell(r, c - 1); }
          else       { c === visColsRef.current.length - 1 ? gotoCell(r + 1, 0) : gotoCell(r, c + 1); }
          break;

        case "ArrowUp":
          if (isF2Mode) break; // F2/dblclick mode → let input handle cursor
          e.preventDefault();
          e.stopPropagation();
          gotoCell(r - 1, c); // typing mode → exit edit + navigate up
          break;

        case "ArrowDown":
          if (isF2Mode) break; // F2/dblclick mode → let input handle cursor
          e.preventDefault();
          e.stopPropagation();
          gotoCell(r + 1, c); // typing mode → exit edit + navigate down
          break;

        case "ArrowLeft":
          if (isF2Mode) break; // F2/dblclick mode → let input move cursor left
          e.preventDefault();
          e.stopPropagation();
          gotoCell(r, c - 1); // typing mode → navigate to prev cell
          break;

        case "ArrowRight":
          if (isF2Mode) break; // F2/dblclick mode → let input move cursor right
          e.preventDefault();
          e.stopPropagation();
          gotoCell(r, c + 1); // typing mode → navigate to next cell
          break;

        default:
          break;
      }
      return;
    }

    // ── NAVIGATION MODE ────────────────────────────────────────────────────
    switch (e.key) {
      case "Tab":
        e.preventDefault();
        if (shift) { c === 0 ? gotoCell(r - 1, visColsRef.current.length - 1) : gotoCell(r, c - 1); }
        else       { c === visColsRef.current.length - 1 ? gotoCell(r + 1, 0) : gotoCell(r, c + 1); }
        break;

      case "Enter":
        e.preventDefault();
        e.stopPropagation();
        gotoCell(r + 1, c);
        break;

      case "F2":
        e.preventDefault();
        enterEditMode(r, c, null); // enter edit mode, keep existing value
        break;

      case "ArrowUp":
        e.preventDefault();
        e.stopPropagation();
        gotoCell(r - 1, c, shift);
        break;

      case "ArrowDown":
        e.preventDefault();
        e.stopPropagation();
        gotoCell(r + 1, c, shift);
        break;

      case "ArrowLeft":
        e.preventDefault();
        e.stopPropagation();
        gotoCell(r, c - 1, shift);
        break;

      case "ArrowRight":
        e.preventDefault();
        e.stopPropagation();
        gotoCell(r, c + 1, shift);
        break;

      case "Delete":
      case "Backspace": {
        const focRow = rowsRef.current[r];
        if (focRow && focRow.Id > 0 && !unlockedIds.has(focRow.Id)) break; // locked
        e.preventDefault(); clearSel();
        break;
      }

      case " ":
        // Space on select/boolean → open the dropdown
        if (col.type === "select" || col.type === "boolean") {
          const focRow2 = rowsRef.current[r];
          if (focRow2 && focRow2.Id > 0 && !unlockedIds.has(focRow2.Id)) break; // locked
          e.preventDefault();
          openDropdownRef.current = true;
          enterEditMode(r, c, null);
        }
        break;

      default:
        // Printable character → overwrite cell and enter edit mode (text-like types only)
        if (isTextLike && e.key.length === 1) {
          const focRow3 = rowsRef.current[r];
          if (focRow3 && focRow3.Id > 0 && !unlockedIds.has(focRow3.Id)) break; // locked
          enterEditMode(r, c, e.key);
        }
        break;
    }
  }, [gotoCell, enterEditMode, setCell, doCopy, doPaste, clearSel, selectAll, unlockedIds, doUndo, doRedo]);

  // Grid container keydown — in nav mode grid itself has focus so all events come here directly.
  // In edit mode the input has focus; its bubbled events are ignored here to prevent double-handling.
  const handleGridKeyDown = useCallback((e) => {
    if (e.target !== gridRef.current) {
      // Bubbled from input/select in edit mode — handleKey already ran on that element
      // In f2/dblclick mode: let arrow keys pass through so cursor moves inside text natively.
      // In typing mode: handleKey already called preventDefault+stopPropagation, nothing to do.
      // Only block scroll if NOT in f2 mode (where native cursor movement must be preserved).
      const isF2Mode = editModeTypeRef.current === "f2";
      if (!isF2Mode && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
      }
      return;
    }
    handleKey(e);
  }, [handleKey]);

  // ── Cell click ────────────────────────────────────────────────────────────
  const handleCellClick = useCallback((r, c, e) => {
    if (e.shiftKey) {
      setEditMode(false);
      setFocus({ r, c }); // anchor fixed → extend selection
    } else {
      gotoCell(r, c); // sets anchor, focus, exits edit mode
    }
  }, [gotoCell]);

  // ── Delete row ────────────────────────────────────────────────────────────
  const delRow = (idx) => {
    pushHistory();
    setEditMode(false);
    const deletedId = rows[idx]?.Id;
    if (deletedId > 0) setUnlockedIds((prev) => { const n = new Set(prev); n.delete(deletedId); return n; });
    if (rows.length === 1) {
      setRows([emptyRow()]); setFocus({ r: 0, c: 0 }); setAnchor({ r: 0, c: 0 });
      return;
    }
    setRows((prev) => prev.filter((_, i) => i !== idx));
    const fix = (pos) => ({ ...pos, r: Math.max(0, pos.r >= idx ? pos.r - 1 : pos.r) });
    setFocus(fix);
    setAnchor(fix);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const valid = rows.filter((r) => r.Name?.trim());
    if (!valid.length) { alert("Koi bhi Firm Name nahi bhari hai."); return; }
    // Block save if any duplicate names exist
    const nameCount = {};
    valid.forEach((r) => { const n = r.Name.trim().toLowerCase(); nameCount[n] = (nameCount[n] || 0) + 1; });
    if (Object.values(nameCount).some((c) => c > 1)) {
      alert("Duplicate Firm Names hain! Red highlighted cells theek karo pehle.");
      return;
    }


    // Build payload — Id: existing Id (edit) or 0 (new), BranchId: existing or 0
    const payload = valid.map((r) => ({
      Id:       r.Id       ?? 0,
      BranchId: r.BranchId ?? 0,
      ...Object.fromEntries(COLS.map((col) => [col.key, r[col.key] ?? ""])),
    }));
    const editRows = payload.filter((r) => r.Id > 0);
    const newRows  = payload.filter((r) => r.Id === 0);
    console.log("=== Multi Firm Entry — Payload ===");
    console.log("Payload with Id and BranchId:", JSON.stringify(payload, null, 2));
    console.table(payload.map((r) => ({ Id: r.Id, BranchId: r.BranchId, Name: r.Name })));
    
    // Validate BranchId for existing records
    const invalidRows = payload.filter(r => r.Id > 0 && (!r.BranchId || r.BranchId === 0));
    if (invalidRows.length > 0) {
      console.warn("Warning: Some existing rows missing BranchId:", invalidRows);
    }
    
    alert(`${newRows.length} new + ${editRows.length} edit — data console mein log kar diya. BranchId properly included for existing records.`);
        const formData = new FormData();
        formData.append("Json", JSON.stringify(payload));
        formData.append("UserId", 0);
       const res = await    Fn_AddEditData(
                dispatch,
                setState,
                { arguList: { id: 0, formData } },
                'FirmMasterFromJson/0/token',
                true,
                "memberid",
                navigate,
                "#"
              );
              console.log("Save response:", res);
              if (res && res?.id > 0) {
                toast.success("Firms saved successfully!", { autoClose: 3000 });
                await FetchAllFirms();
              } else {
                toast.error("Save failed. Please try again.", { autoClose: 4000 });
              }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER INPUT for focused (active editing) cell
  // ─────────────────────────────────────────────────────────────────────────
  const renderInput = (r, c) => {
    const col = visCOLS[c];
    const val = rows[r]?.[col.key] ?? "";

    const shared = {
      ref: inputRef,
      style: {
        width: "100%", height: "100%", border: "none", outline: "none",
        background: "transparent", padding: "0 4px", fontSize: "13px",
        boxSizing: "border-box",
      },
      onKeyDown: handleKey,
      onPaste: (e) => {
        const text = e.clipboardData?.getData("text");
        if (text && (text.includes("\t") || text.includes("\n"))) {
          e.preventDefault();
          doPaste(text, r, c);
        }
      },
    };

    if (col.type === "select") {
      return (
        <select {...shared} value={val} onChange={(e) => {
          setCell(r, col.key, e.target.value);
          c === visCOLS.length - 1 ? gotoCell(r + 1, 0) : gotoCell(r, c + 1);
        }}>
          <option value="">{col.placeholder ?? "-- Select --"}</option>
          {(dropdowns[col.optKey] || []).map((o) => (
            <option key={o.Id} value={o.Id}>{o.Name}</option>
          ))}
        </select>
      );
    }
    if (col.type === "boolean") {
      return (
        <select {...shared} value={val} onChange={(e) => {
          setCell(r, col.key, e.target.value);
          c === visCOLS.length - 1 ? gotoCell(r + 1, 0) : gotoCell(r, c + 1);
        }}>
          <option value="">{col.placeholder ?? "-- Select --"}</option>
          <option value="false">No</option>
          <option value="true">Yes</option>
        </select>
      );
    }
    return (
      <input
        {...shared}
        type={col.type === "number" ? "number" : col.type === "time" ? "time" : col.type === "date" ? "date" : "text"}
        value={val}
        onChange={(e) => setCell(r, col.key, e.target.value)}
      />
    );
  };

  const filledCount = rows.filter((r) => r.Name?.trim()).length;
  const isMultiSel  = selRange.r2 > selRange.r1 || selRange.c2 > selRange.c1;

  // Duplicate FirmName detection (real-time, case-insensitive)
  const duplicateNameRows = (() => {
    const nameCount = {};
    rows.forEach((row) => {
      const n = row.Name?.trim().toLowerCase();
      if (n) nameCount[n] = (nameCount[n] || 0) + 1;
    });
    const s = new Set();
    rows.forEach((row, i) => {
      const n = row.Name?.trim().toLowerCase();
      if (n && nameCount[n] > 1) s.add(i);
    });
    return s;
  })();

  // ─────────────────────────────────────────────────────────────────────────
  // JSX
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 4rem)", overflow: "hidden", margin: "-2.5rem -2.5rem 0 -2.5rem" }}>
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px",
        padding: "6px 12px", background: "#e8f5e9",
        borderBottom: "2px solid #217346", flexShrink: 0,
      }}>
        <span style={{ fontSize: "14px", fontWeight: 700, color: "#217346", marginRight: "4px" }}>
          <i className="fas fa-table me-2"></i>Multi Firm Entry
        </span>

        <button className="btn btn-sm btn-outline-success" style={{ fontSize: "12px", padding: "2px 10px" }}
          onClick={addNewRow}>
          <i className="fas fa-plus me-1"></i>Add Row
        </button>
        <button className="btn btn-sm btn-outline-danger" style={{ fontSize: "12px", padding: "2px 10px" }}
          onClick={() => {
            if (window.confirm("Saara data clear ho jayega. Confirm?")) {
              pushHistory();
              setRows(Array.from({ length: 10 }, emptyRow));
              setFocus({ r: 0, c: 0 }); setAnchor({ r: 0, c: 0 }); setEditMode(false);
            }
          }}>
          <i className="fas fa-trash me-1"></i>Clear All
        </button>
        <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: "12px", padding: "2px 10px" }}
          onClick={() => navigate("/FirmMaster")}>
          <i className="fas fa-arrow-left me-1"></i>Back
        </button>

        <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: "12px", padding: "2px 10px" }}
          onClick={doUndo} title="Undo (Ctrl+Z)">
          <i className="fas fa-undo me-1"></i>Undo
        </button>
        <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: "12px", padding: "2px 10px" }}
          onClick={doRedo} title="Redo (Ctrl+Y)">
          <i className="fas fa-redo me-1"></i>Redo
        </button>

        <div ref={colPickerRef} style={{ position: "relative" }}>
          <button className="btn btn-sm btn-outline-primary" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={() => setColPickerOpen((p) => !p)} title="Show/Hide Columns">
            <i className="fas fa-columns me-1"></i>Columns ({visCOLS.length}/{COLS.length})
          </button>
          {colPickerOpen && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 200, background: "#fff", border: "1px solid #d0d0d0", borderRadius: "6px", boxShadow: "0 4px 18px rgba(0,0,0,0.18)", minWidth: "230px", maxHeight: "70vh", overflowY: "auto" }}>
              <div style={{ padding: "6px 12px", borderBottom: "1px solid #eee", position: "sticky", top: 0, background: "#fff", zIndex: 1, display: "flex", gap: "6px", alignItems: "center" }}>
                <span style={{ fontSize: "11px", fontWeight: 700, color: "#444", flex: 1 }}>Column Visibility</span>
                <button onClick={() => setVisibleKeys(new Set(COLS.map(c => c.key)))} style={{ fontSize: "10px", padding: "1px 8px", border: "1px solid #217346", borderRadius: "3px", background: "#e8f5e9", color: "#217346", cursor: "pointer" }}>All</button>
                <button onClick={() => setVisibleKeys(new Set())} style={{ fontSize: "10px", padding: "1px 8px", border: "1px solid #dc3545", borderRadius: "3px", background: "#fff0f0", color: "#dc3545", cursor: "pointer" }}>None</button>
              </div>
              {COL_GROUPS.map((grp) => {
                const startIdx = COLS.findIndex(c => c.key === grp.startKey);
                const endIdx   = COLS.findIndex(c => c.key === grp.endKey);
                if (startIdx < 0 || endIdx < 0) return null;
                const grpCols = COLS.slice(startIdx, endIdx + 1);
                return (
                  <div key={grp.label}>
                    <div style={{ padding: "3px 12px", background: grp.color, color: "#fff", fontSize: "11px", fontWeight: 700, letterSpacing: "0.4px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span>{grp.label}</span>
                      <span style={{ fontSize: "10px", opacity: 0.8 }}>{grpCols.filter(c => visibleKeys.has(c.key)).length}/{grpCols.length}</span>
                    </div>
                    {grpCols.map((col) => (
                      <label key={col.key} style={{ display: "flex", alignItems: "center", gap: "7px", padding: "3px 16px", cursor: "pointer", fontSize: "12px", userSelect: "none", color: "#333" }}>
                        <input type="checkbox" checked={visibleKeys.has(col.key)} onChange={(e) => setVisibleKeys((prev) => { const next = new Set(prev); e.target.checked ? next.add(col.key) : next.delete(col.key); return next; })} />
                        {col.label}
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <span style={{ fontSize: "11px", color: "#444", marginLeft: "4px" }}>
          <kbd style={KBD}>↑↓←→</kbd> Navigate &nbsp;
          <kbd style={KBD}>F2</kbd>/<kbd style={KBD}>DblClick</kbd> Edit &nbsp;
          <kbd style={KBD}>Type</kbd> Overwrite &nbsp;
          <kbd style={KBD}>Esc</kbd> Restore &nbsp;
          <kbd style={KBD}>Shift+Arrow</kbd> Select &nbsp;
          <kbd style={KBD}>Ctrl+C/V</kbd> Copy/Paste &nbsp;
          <kbd style={KBD}>Del</kbd> Clear
        </span>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "12px", fontWeight: 700, color: "#217346" }}>
            {filledCount} / {rows.length} rows filled
          </span>
          {/* Bulk Edit / Lock toggle for all existing rows */}
          {rows.some(r => r.Id > 0) && (
            unlockedIds.size > 0 ? (
              <button className="btn btn-sm btn-warning" style={{ padding: "3px 14px", fontSize: "13px" }}
                onClick={() => setUnlockedIds(new Set())}>
                <i className="fas fa-lock me-1"></i>Lock All
              </button>
            ) : (
              <button className="btn btn-sm btn-warning" style={{ padding: "3px 14px", fontSize: "13px" }}
                onClick={() => setUnlockedIds(new Set(rows.filter(r => r.Id > 0).map(r => r.Id)))}>
                <i className="fas fa-edit me-1"></i>Edit
              </button>
            )
          )}
          <button className="btn btn-sm btn-success" style={{ padding: "3px 14px", fontSize: "13px" }}
            onClick={handleSave}>
            <i className="fas fa-save me-1"></i>Save (Console)
          </button>
        </div>
      </div>

      {/* ─── Pagination Toolbar ─── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 16px",
        background: "#e8f5e9",
        borderBottom: "1px solid #ced4da",
        fontSize: "12px",
        flexShrink: 0,
        userSelect: "none"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontWeight: 600, color: "#217346" }}>Show:</span>
          <select
            value={pageSize}
            onChange={e => handlePageSizeChange(parseInt(e.target.value))}
            style={{ padding: "2px 6px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "12px", outline: "none", background: "#fff", cursor: "pointer" }}
          >
            <option value={10}>10 rows</option>
            <option value={20}>20 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
          </select>
          <span style={{ color: "#555" }}>
            Showing <strong>{rows.length > 0 ? startIndex + 1 : 0}</strong> - <strong>{endIndex}</strong> of <strong>{rows.length}</strong> rows
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === 1 ? "default" : "pointer" }}
          >
            « First
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === 1 ? "default" : "pointer" }}
          >
            ‹ Prev
          </button>
          <span style={{ padding: "0 8px", fontWeight: 700, color: "#217346" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === totalPages ? "default" : "pointer" }}
          >
            Next ›
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === totalPages ? "default" : "pointer" }}
          >
            Last »
          </button>
        </div>
      </div>

      {/* ── Spreadsheet Grid ─────────────────────────────────────────────── */}
      <div
        ref={gridRef}
        tabIndex={0}
        onKeyDown={handleGridKeyDown}
        style={{ flex: 1, overflowX: "auto", overflowY: "auto", outline: "none", background: "#fff" }}
      >
        <table style={{ borderCollapse: "collapse", tableLayout: "fixed", fontSize: "13px", userSelect: "none" }}>
          {/* ──── HEADER ──── */}
          <thead>
            <tr>
              <th style={TH_NUM}></th>
              {visCOLS.map((col, ci) => (
                <th key={ci} style={{ ...thCol(col.w, ci >= selRange.c1 && ci <= selRange.c2), minWidth: `${col.w}px`, width: `${col.w}px` }}>
                  <span style={{ marginRight: "4px", opacity: 0.6, fontWeight: 400, fontSize: "10px" }}>
                    {colLetter(ci)}
                  </span>
                  {col.label}
                </th>
              ))}
              <th style={{ ...TH_NUM, width: "36px", minWidth: "36px" }}></th>
              <th style={{ ...TH_NUM, width: "50px", minWidth: "50px" }}></th>
            </tr>
          </thead>

          {/* ──── BODY ──── */}
          <tbody>
            {rows.slice(startIndex, endIndex).map((row, localRi) => {
              const ri = startIndex + localRi;
              return (
                <tr key={ri}>
                  {/* Row number — locked=orange, unlocked=green, new=gray */}
                  <td style={{ ...rnTd(ri, focus.r), background: focus.r === ri ? "#dbeafe" : row.Id > 0 ? (unlockedIds.has(row.Id) ? "#e8f5e9" : "#fff3e0") : "#f1f5f9", color: row.Id > 0 ? (unlockedIds.has(row.Id) ? "#217346" : "#e65100") : "#64748b" }}
                    title={row.Id > 0 ? (unlockedIds.has(row.Id) ? `Editing (Id: ${row.Id})` : `Locked (Id: ${row.Id}) — click Edit to unlock`) : "New row"}>
                    {ri + 1}
                  </td>

                  {/* Data cells */}
                  {visCOLS.map((col, ci) => {
                    const focused  = isFoc(ri, ci);
                    const selected = inSel(ri, ci);
                    return (
                      <td
                        key={ci}
                        ref={focused ? focusedCellRef : null}
                        style={{ ...tdStyle(focused, selected, ri, focused && editMode, col.key === "Name" && duplicateNameRows.has(ri),
                          row.Id > 0 ? (unlockedIds.has(row.Id) ? "#f0fdf4" : "#fff8f0") : null), minWidth: `${col.w}px`, width: `${col.w}px` }}
                        onMouseDown={(e) => handleCellClick(ri, ci, e)}
                        onDoubleClick={() => { if (!(row.Id > 0 && !unlockedIds.has(row.Id))) enterEditMode(ri, ci, null); }}
                      >
                        {focused && editMode ? (
                          renderInput(ri, ci)
                        ) : (() => {
                          const dv = displayVal(col, row[col.key]);
                          const isEmptySel = !dv && (col.type === "select" || col.type === "boolean");
                          return (
                            <span style={{
                              display: "block", padding: "0 4px", overflow: "hidden",
                              textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "26px",
                              color: isEmptySel ? "#b0b7c3" : row[col.key] ? "#212529" : "#ccc",
                              fontWeight: focused ? 600 : 400,
                              fontStyle: isEmptySel ? "italic" : "normal",
                            }}>
                              {isEmptySel
                                ? (col.placeholder?.replace(/^-- | --$/g, "") ?? "Select...")
                                : (dv || "\u00A0")}
                            </span>
                          );
                        })()}
                      </td>
                    );
                  })}

                  {/* Edit / Lock button — only for existing (Id > 0) rows */}
                  <td style={{ width: "50px", minWidth: "50px", height: "28px", border: "1px solid #d0d0d0", textAlign: "center", background: "#fafafa", padding: "2px" }}>
                    {row.Id > 0 ? (
                      unlockedIds.has(row.Id) ? (
                        <button onClick={() => setUnlockedIds((prev) => { const n = new Set(prev); n.delete(row.Id); return n; })}
                          title="Lock row" style={{ border: "1px solid #aaa", borderRadius: "3px", background: "#e8f5e9", color: "#217346", fontSize: "10px", padding: "1px 4px", cursor: "pointer", width: "100%" }}>
                          🔓 Lock
                        </button>
                      ) : (
                        <button onClick={() => setUnlockedIds((prev) => new Set([...prev, row.Id]))}
                          title="Unlock row for editing" style={{ border: "1px solid #aaa", borderRadius: "3px", background: "#fff3e0", color: "#e65100", fontSize: "10px", padding: "1px 4px", cursor: "pointer", width: "100%" }}>
                          🔒 Edit
                        </button>
                      )
                    ) : null}
                  </td>

                  {/* Delete row button */}
                  <td style={{
                    width: "36px", minWidth: "36px", height: "28px",
                    border: "1px solid #d0d0d0", textAlign: "center",
                    background: "#fafafa", padding: 0,
                  }}>
                    <button
                      onClick={() => delRow(ri)}
                      title="Row delete"
                      style={{
                        border: "none", background: "none", color: "#dc3545",
                        fontSize: "18px", lineHeight: 1, cursor: "pointer",
                        width: "100%", height: "100%", fontWeight: 700,
                      }}
                    >×</button>
                  </td>
                </tr>
              );
            })}

            {/* Click-to-add row */}
            <tr>
              <td
                colSpan={visCOLS.length + 3}
                onClick={addNewRow}
                style={{
                  border: "1px dashed #b0b0b0", textAlign: "center", color: "#aaa",
                  fontSize: "12px", cursor: "pointer", height: "28px",
                  background: "#fafafa", userSelect: "none",
                }}
              >
                ＋ Click here to add a new row
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Status Bar ───────────────────────────────────────────────────── */}
      <div style={{
        flexShrink: 0, padding: "3px 12px", background: "#e8f5e9",
        borderTop: "1px solid #a5d6a7", fontSize: "12px", color: "#555",
        display: "flex", gap: "24px", alignItems: "center",
      }}>
        <span>
          Cell: <strong style={{ color: "#217346" }}>{colLetter(focus.c)}{focus.r + 1}</strong>
        </span>
        {isMultiSel && (
          <span>
            Selection:{" "}
            <strong>
              {colLetter(selRange.c1)}{selRange.r1 + 1} → {colLetter(selRange.c2)}{selRange.r2 + 1}
            </strong>{" "}
            ({selRange.r2 - selRange.r1 + 1}R × {selRange.c2 - selRange.c1 + 1}C)
          </span>
        )}
        <span style={{ marginLeft: "auto", color: "#666" }}>
          <i className="fas fa-info-circle me-1" style={{ color: "#217346" }}></i>
          Excel se copy karke Ctrl+V se directly paste karo — multi-cell auto-fill hoga
        </span>
      </div>
    </div>
  );
};

export default MultiFirmEntry;
